/**
 * The copy dictionary (HANDOFF §1.3).
 *
 * The rule it enforces: **the data layer and the API speak generic English;
 * only the presentation layer speaks brand.** A like is a `likes` row, a
 * `POST /tracks/{id}/like`, a `like_count` column, and a `track.delete.any`
 * permission. What it is *called* is decided here and nowhere else.
 *
 * ## This file has already earned its keep
 *
 * These strings were rewritten wholesale when `dev/design` arrived, because the
 * design's vocabulary is not the one §1.3's examples assumed. A like is a
 * **save**, not a "Vibe". A follower is a **follower**, not a "Viber" - the
 * brand's metaphor shows up in "vibed with" (who you have been listening to)
 * rather than in the follow graph at all. Live radio is **Rooms**, and
 * uploading is **dropping a track**.
 *
 * That is six words changed in one file and not a single database column, API
 * route, or permission key touched. If any of it had leaked into the schema the
 * way §1.3 warns about, this would have been a migration on a live system and a
 * breaking API change instead.
 *
 * There is no i18n here and none planned. The indirection is the point;
 * translation would just be a second consumer of it.
 */

import { brand } from './brand';

export const copy = {
  /*
   * A like is a favourite.
   *
   * The design's mockups say "Save"; the product calls it Favorites. This line
   * is the entire disagreement - the API calls it a like throughout (`likes`,
   * `like_count`, `POST /tracks/{id}/like`, and a `track.like` permission) and
   * not one of those changed when the word did.
   *
   * That is the second time §1.3 has paid for itself here. The first was the
   * whole dictionary being rewritten when `dev/design` arrived. Had any of it
   * reached the schema, this would have been a migration and a breaking API
   * change instead of five strings.
   */
  'action.like': 'Favorite',
  'action.liked': 'Favorited',
  'action.unlike': 'Remove favorite',
  'noun.like': 'favorite',
  'noun.like.plural': 'favorites',

  'action.follow': 'Follow',
  'action.following': 'Following',
  'noun.follower': 'follower',
  'noun.follower.plural': 'followers',

  /*
   * Live radio is "Rooms" (§6.8 calls the table `stations`, and it stays
   * `stations`). Phase 7, but the word is fixed now so nothing has to be
   * renamed in the UI later.
   */
  'noun.station': 'Room',
  'noun.station.plural': 'Rooms',

  /*
   * A repost is a rewind.
   *
   * The table is `reposts`, the column is `repost_count`, the route is
   * `POST /tracks/{id}/repost`. Only these five strings know the word.
   */
  'action.repost': 'Rewind',
  'action.reposted': 'Rewound',
  'action.unrepost': 'Undo rewind',
  'noun.repost': 'Rewind',
  'noun.repost.plural': 'Rewinds',

  /*
   * An album is a `collections` row with `type = 'album'` (§6.2), which is also
   * where EPs, singles and playlists will live. "Album" is what artists call
   * the thing they are making; "collection" is what the table is called.
   */
  'noun.collection': 'Album',
  'noun.collection.plural': 'Albums',
  /*
   * The earned boost, which is the one place the metaphor is load-bearing.
   *
   * The server calls this tier `earned` everywhere - enum, API, weighting -
   * because §1.3 keeps the brand out of the data layer. The product name lives
   * here, and a rename is this line rather than a migration on a live database.
   */
  'noun.boost.earned': 'Vibed Boost',
  'noun.boost.earned.plural': 'Vibed Boosts',

  'noun.track': 'Track',
  'noun.track.plural': 'Tracks',

  'cta.newAlbum': 'New album',
  'cta.editAlbum': 'Edit album',
  'cta.addTracks': 'Add tracks',

  // Top-level navigation, matching the design.
  'nav.discover': 'Discover',
  'nav.following': 'Following',
  'nav.rooms': 'Rooms',
  'nav.library': 'Library',
  'nav.favorites': 'Favorites',

  'cta.signup': `Join ${brand.name}`,
  'cta.login': 'Sign in',
  // Not "Upload". The design's phrase, and a better one - it describes the
  // event rather than the file operation.
  'cta.upload': 'Drop a track',
  'cta.message': 'Message',

  // Discovery labels. Each is tied to a colour in the design and the pairing is
  // deliberate: Fresh for anything new, Hot for anything live.
  'label.firstUpload': 'first upload',
  'label.newHere': 'new here',
  'label.rising': 'rising',
  'label.live': 'live',
} as const;

export type CopyKey = keyof typeof copy;

/** Look up a phrase. Typed, so a typo is a build error rather than a blank UI. */
export function t(key: CopyKey): string {
  return copy[key];
}

/** `1 follower` / `12 followers`, without the call site owning the plural rule. */
export function plural(count: number, singular: CopyKey, many: CopyKey): string {
  return `${count.toLocaleString()} ${count === 1 ? copy[singular] : copy[many]}`;
}
